import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import TableColumnFilter from '../../components/TableColumnFilter';
import ActiveFilterChips, { FilterChip } from '../../components/ActiveFilterChips';
import { usePersistListFiltersSession } from '../../hooks/usePersistListFiltersSession';
import { LIST_FILTER_SESSION_KEYS } from '../../constants/listFilterSessionKeys';

import {
  PencilIcon,
  EyeIcon,
  PlusIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  UserIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';

interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  name: string;
  displayName: string | null;
  email: string;
  status: string;
  title: string | null;
  businessPhone: string | null;
  mobilePhone: string | null;
  customerId: string | null;
  isPrimary: boolean;
  customer: {
    id: string;
    accountName: string;
  } | null;
  createdAt: string;
  updatedAt: string;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const ContactList: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  usePersistListFiltersSession('/contacts', LIST_FILTER_SESSION_KEYS.contacts);

  const patchSearchParams = useCallback(
    (updates: Record<string, string | null | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, val] of Object.entries(updates)) {
            if (val === null || val === undefined || val === '') {
              next.delete(key);
            } else {
              next.set(key, val);
            }
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const currentPage = useMemo(() => {
    const p = parseInt(searchParams.get('page') || '1', 10);
    return Number.isFinite(p) && p >= 1 ? p : 1;
  }, [searchParams]);

  const pageSize = useMemo(() => {
    const ps = parseInt(searchParams.get('pageSize') || '10', 10);
    return PAGE_SIZE_OPTIONS.includes(ps) ? ps : 10;
  }, [searchParams]);

  const searchTerm = searchParams.get('search') ?? '';
  const statusFilter = searchParams.get('status') ?? '';
  const customerFilter = searchParams.get('customerId') ?? '';
  const filterName = searchParams.get('name') ?? '';
  const filterEmail = searchParams.get('email') ?? '';
  const filterPhone = searchParams.get('phone') ?? '';
  const filterTitle = searchParams.get('title') ?? '';
  const filterPrimary = searchParams.get('isPrimary') ?? '';

  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') ?? '');
  useEffect(() => {
    setSearchInput(searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    const id = setTimeout(() => {
      const trimmed = searchInput.trim();
      const current = searchParams.get('search') ?? '';
      if (trimmed === current) return;
      patchSearchParams({ search: trimmed || null, page: '1' });
    }, 300);
    return () => clearTimeout(id);
  }, [searchInput, searchParams, patchSearchParams]);

  const queryClient = useQueryClient();

  // Fetch contacts
  const { data, isLoading, error } = useQuery({
    queryKey: ['contacts', currentPage, pageSize, searchTerm, statusFilter, customerFilter, filterName, filterEmail, filterPhone, filterTitle, filterPrimary],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: pageSize.toString(),
        offset: ((currentPage - 1) * pageSize).toString(),
      });

      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter) params.append('status', statusFilter);
      if (customerFilter) params.append('customerId', customerFilter);
      if (filterName) params.append('name', filterName);
      if (filterEmail) params.append('email', filterEmail);
      if (filterPhone) params.append('phone', filterPhone);
      if (filterTitle) params.append('title', filterTitle);
      if (filterPrimary) params.append('isPrimary', filterPrimary);

      const response = await api.get(`/contacts?${params}`);
      return response.data;
    },
    refetchOnWindowFocus: true,
    refetchOnMount: true
  });

  // Fetch customers for filter dropdown
  const { data: customersData } = useQuery({
    queryKey: ['customers-dropdown'],
    queryFn: async () => {
      const response = await api.get('/customers/dropdown');
      return response.data.customers || [];
    }
  });

  // Delete contact mutation
  const deleteMutation = useMutation({
    mutationFn: async (contactId: string) => {
      await api.delete(`/contacts/${contactId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      toast.success('Contact deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to delete contact');
    }
  });

  const handleDelete = (contact: Contact) => {
    confirmAlert({
      title: 'Confirm Deletion',
      message: `Are you sure you want to delete the contact "${contact.name}"?`,
      buttons: [
        {
          label: 'Yes',
          onClick: () => deleteMutation.mutate(contact.id)
        },
        {
          label: 'No',
          onClick: () => {}
        }
      ]
    });
  };

  const customerOptions = useMemo(
    () =>
      (customersData || []).map((c: { id: string; accountName: string }) => ({
        value: c.id,
        label: c.accountName,
      })),
    [customersData]
  );

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'pending', label: 'Pending' },
    { value: 'suspended', label: 'Suspended' },
    { value: 'inactive', label: 'Inactive' },
  ];

  const renderColumnHeader = useCallback(
    (columnKey: string, columnLabel: string) => {
      switch (columnKey) {
        case 'name':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterName}
              placeholder="Filter by name..."
              onChange={(v) => {
                patchSearchParams({ name: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'title':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterTitle}
              placeholder="Filter by title..."
              onChange={(v) => {
                patchSearchParams({ title: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'email':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterEmail}
              placeholder="Filter by email..."
              onChange={(v) => {
                patchSearchParams({ email: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'customer':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="searchable-select"
              options={customerOptions}
              value={customerFilter}
              placeholder="All Customers"
              onChange={(v) => {
                patchSearchParams({ customerId: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'isPrimary':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="single-select"
              options={[
                { value: 'true', label: 'Primary' },
                { value: 'false', label: 'Not Primary' },
              ]}
              value={filterPrimary}
              placeholder="All"
              onChange={(v) => {
                patchSearchParams({ isPrimary: v === 'true' || v === 'false' ? (v as string) : null, page: '1' });
              }}
            />
          );
        case 'status':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="single-select"
              options={statusOptions}
              value={statusFilter}
              placeholder="All Status"
              onChange={(v) => {
                patchSearchParams({ status: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'phone':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterPhone}
              placeholder="Filter by phone..."
              onChange={(v) => {
                patchSearchParams({ phone: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        default:
          return columnLabel;
      }
    },
    [
      filterName,
      filterTitle,
      filterEmail,
      customerOptions,
      customerFilter,
      filterPrimary,
      statusFilter,
      filterPhone,
      patchSearchParams,
    ]
  );

  const activeFilterChips: FilterChip[] = useMemo(() => {
    const chips: FilterChip[] = [];
    if (searchTerm) chips.push({ id: 'search', label: 'Search', value: `"${searchTerm}"`, color: 'blue' });
    if (filterName) chips.push({ id: 'name', label: 'Name', value: filterName, color: 'blue' });
    if (filterTitle) chips.push({ id: 'title', label: 'Title', value: filterTitle, color: 'blue' });
    if (filterEmail) chips.push({ id: 'email', label: 'Email', value: filterEmail, color: 'blue' });
    if (customerFilter) {
      const customer = customerOptions.find((c: { value: string; label: string }) => c.value === customerFilter);
      chips.push({ id: 'customerId', label: 'Customer', value: customer?.label || 'Unknown', color: 'teal' });
    }
    if (filterPrimary) {
      chips.push({ id: 'isPrimary', label: 'Primary', value: filterPrimary === 'true' ? 'Yes' : 'No', color: 'green' });
    }
    if (statusFilter) {
      const statusLabel = statusOptions.find((s) => s.value === statusFilter)?.label || statusFilter;
      chips.push({ id: 'status', label: 'Status', value: statusLabel, color: 'purple' });
    }
    if (filterPhone) chips.push({ id: 'phone', label: 'Phone', value: filterPhone, color: 'gray' });
    return chips;
  }, [searchTerm, filterName, filterTitle, filterEmail, customerFilter, filterPrimary, statusFilter, filterPhone, customerOptions]);

  const handleRemoveFilterChip = (chipId: string) => {
    switch (chipId) {
      case 'search':
        setSearchInput('');
        patchSearchParams({ search: null, page: '1' });
        break;
      case 'name':
        patchSearchParams({ name: null, page: '1' });
        break;
      case 'title':
        patchSearchParams({ title: null, page: '1' });
        break;
      case 'email':
        patchSearchParams({ email: null, page: '1' });
        break;
      case 'customerId':
        patchSearchParams({ customerId: null, page: '1' });
        break;
      case 'isPrimary':
        patchSearchParams({ isPrimary: null, page: '1' });
        break;
      case 'status':
        patchSearchParams({ status: null, page: '1' });
        break;
      case 'phone':
        patchSearchParams({ phone: null, page: '1' });
        break;
    }
  };

  const handleClearAllFilters = () => {
    setSearchInput('');
    patchSearchParams({
      search: null,
      name: null,
      title: null,
      email: null,
      customerId: null,
      isPrimary: null,
      status: null,
      phone: null,
      page: '1',
    });
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'suspended':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          Error loading contacts: {(error as any).message}
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 flex flex-col h-full min-h-0 overflow-hidden">
      {/* Compact Header Bar */}
      <div className="bg-white shadow rounded-lg mb-2 flex-shrink-0">
        <div className="px-4 py-2">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
            {/* Left: Title */}
            <h1 className="text-lg font-semibold text-gray-900 whitespace-nowrap">Contacts</h1>

            {/* Right: Search and New Button */}
            <div className="flex items-center gap-2">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search contacts..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="pl-10 w-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <Link
                to="/contacts/new"
                state={{ contactsListSearch: location.search }}
                className="inline-flex items-center px-3 py-1.5 text-sm border border-transparent rounded-md shadow-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
              >
                <PlusIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
                New
              </Link>
            </div>
          </div>
        </div>
      </div>

      {activeFilterChips.length > 0 && (
        <div className="bg-white shadow rounded-lg mb-2 px-4 py-2 flex-shrink-0">
          <ActiveFilterChips
            chips={activeFilterChips}
            onRemove={handleRemoveFilterChip}
            onClearAll={handleClearAllFilters}
          />
        </div>
      )}

      {/* Contacts Table - Takes remaining space */}
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden flex-1 min-h-0 flex flex-col">
          <div className="overflow-scroll flex-1" style={{ scrollbarGutter: 'stable' }}>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    {renderColumnHeader('name', 'Name')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    {renderColumnHeader('title', 'Title')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    {renderColumnHeader('email', 'Email')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    {renderColumnHeader('customer', 'Customer')}
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    {renderColumnHeader('isPrimary', 'Primary')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    {renderColumnHeader('status', 'Status')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    {renderColumnHeader('phone', 'Phone')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data?.contacts?.map((contact: Contact) => (
                  <tr key={contact.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <UserIcon className="h-8 w-8 text-gray-400 mr-3" />
                        <div className="text-sm font-medium text-gray-900">{contact.name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {contact.title || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{contact.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {contact.customer ? (
                        <div className="flex items-center text-sm text-gray-900">
                          <BuildingOfficeIcon className="h-4 w-4 text-gray-400 mr-1" />
                          {contact.customer.accountName}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {contact.isPrimary ? (
                        <StarIconSolid className="h-5 w-5 text-yellow-500 inline-block" title="Primary Contact" />
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(contact.status)}`}>
                        {contact.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {contact.businessPhone || contact.mobilePhone || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <Link
                          to={`/contacts/${contact.id}`}
                          state={{ contactsListSearch: location.search }}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="View"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </Link>
                        <Link
                          to={`/contacts/${contact.id}/edit`}
                          state={{ contactsListSearch: location.search }}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </Link>
                        <button
                          onClick={() => handleDelete(contact)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data?.contacts?.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">No contacts found</p>
            </div>
          )}
        </div>
      )}

      {/* Pagination Controls - Always visible */}
      <div className="mt-2 flex flex-col sm:flex-row items-center justify-between gap-2 bg-white px-3 py-2 rounded-lg shadow flex-shrink-0" style={{ minHeight: '48px' }}>
        {data?.total > 0 ? (
          <>
            {/* Left: Records per page dropdown */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-700">Show</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  patchSearchParams({
                    pageSize: e.target.value,
                    page: '1',
                  });
                }}
                className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                {PAGE_SIZE_OPTIONS.map((size, index) => {
                  const previousSize = index > 0 ? PAGE_SIZE_OPTIONS[index - 1] : 0;
                  const isDisabled = previousSize >= data.total && size > data.total;
                  return (
                    <option key={size} value={size} disabled={isDisabled}>
                      {size}
                    </option>
                  );
                })}
              </select>
              <span className="text-sm text-gray-700">entries</span>
            </div>

            {/* Center: Entry count */}
            <div className="text-sm text-gray-700">
              Showing {Math.min((currentPage - 1) * pageSize + 1, data.total)} to {Math.min(currentPage * pageSize, data.total)} of {data.total} entries
            </div>

            {/* Right: Pagination buttons */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => patchSearchParams({ page: '1' })}
                disabled={currentPage === 1}
                className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="First page"
              >
                &laquo;
              </button>
              <button
                onClick={() => patchSearchParams({ page: String(currentPage - 1) })}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              {/* Page numbers */}
              {(() => {
                const totalPages = Math.ceil(data.total / pageSize);
                const pages: (number | string)[] = [];

                if (totalPages <= 7) {
                  for (let i = 1; i <= totalPages; i++) pages.push(i);
                } else {
                  if (currentPage <= 3) {
                    pages.push(1, 2, 3, 4, '...', totalPages);
                  } else if (currentPage >= totalPages - 2) {
                    pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
                  } else {
                    pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
                  }
                }

                return pages.map((p, idx) => (
                  p === '...' ? (
                    <span key={`ellipsis-${idx}`} className="px-2 py-1 text-sm text-gray-500">...</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => patchSearchParams({ page: String(p) })}
                      className={`px-3 py-1 text-sm border rounded-md ${
                        currentPage === p
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {p}
                    </button>
                  )
                ));
              })()}

              <button
                onClick={() => patchSearchParams({ page: String(currentPage + 1) })}
                disabled={currentPage >= Math.ceil(data.total / pageSize)}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
              <button
                onClick={() =>
                  patchSearchParams({
                    page: String(Math.ceil(data.total / pageSize)),
                  })
                }
                disabled={currentPage >= Math.ceil(data.total / pageSize)}
                className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Last page"
              >
                &raquo;
              </button>
            </div>
          </>
        ) : (
          <div className="text-sm text-gray-500 w-full text-center">No records</div>
        )}
      </div>
    </div>
  );
};

export default ContactList;